<template>
   <!-- TODO:
   -->
   <!--
      WHAT: The UI for collecting the Sendgrid's Email app configuration
      HISTORY:
      - V250813.1: Added an unsubscribe link to the email bodies if it didn’t exist and warned users accordingly, replaced the NOTE with a <v-alert> component, and used isLocal instead of isActualEndpoint.
      - V250225.1: Introduced VAR_WITH_DEFAULT_PATTERN instead of VAR_PATTERN to tighten the variables validation with default value.
      - V250214.1: Changed mdi-code-braces icon to data_object as it didn't show on Aref side.
      - V250203.1: Tightened all the validations (according to the following business logic) to avoid unsupported values + Modified the UI hints to be more descriptive.
      - V250106.1: Changed replyTo rules from toAddress to email in order to force it to be an static email address only (SG doesn't accept variable).
      - V231207.3: [on Aref side} Passed ' ' for appEndpoint to read actions/triggers with pull mechanism (rather than push).
      - V231207.2: Made toAddress's hint a variable as {{ created issue.
      - V231207.1: Consumed the actual appEndpoint from AWS Lambda function + Forced {{}} to be used for field names.
      - V231109.1: Handled consumerCode and appEndpoint as part of the input/output properties.
      - V230222.1: Added isActualEndpoint prop + Changed ## to {}.
      - V230221.1: 1st release.
   -->
   <!-- BUSINESS LOGIC:
      - From Email: A valid email address only (no variable).
      - From Name: No variable
      - To Email(s):
         For bulk: Only variables
         For trigger: A mix of variable and valid emails.
      - Reply-to Email: A valid email address only (no variable)
      - Categories: No variables, max of 9, no longer than 255
   -->
<div>
   <v-tooltip left>
      <template v-slot:activator="{ on }">
         <v-btn text icon
            v-on="on"
            @click="btnClicked"
         >
            <v-icon>{{btnIconName}}</v-icon>
         </v-btn>
      </template>
      <span>{{btnTooltip}}</span>
   </v-tooltip>

   <v-dialog no-click-animation persistent scrollable
      max-width="1080px"
      v-model="dialog"
   >
      <v-card flat>
         <v-card-title class="title grey--text darken-4 font-weight-bold">Settings for {{appName}}
            <div class="flex-grow-1"></div>
            <v-btn v-if="isLocal"
               color="primary"
               class="caption text-capitalize pr-4"
               @click="populateSettings"
            >Populate Settings</v-btn>
         </v-card-title>

         <v-card-text>
            <v-alert dense outlined prominent text
               class="mb-0"
               border="left"
               type="info"
            >
               <ul class="pl-2">
                  <li>Wherever is marked with <v-icon small>data_object</v-icon>, you can use variables with the following syntax to customize your email: <code>{{variableSyntax}}</code></li>
                  <li>Use <code>{{unsubscribeSyntax}}</code> in your email bodies to insert an unsubscribe link. If you don’t include it, the system will add the link automatically at the end. Clicking the link opens a page where the recipient can unsubscribe from this app.</li>
               </ul>
            </v-alert>
            <v-form lazy-validation
               ref="mainForm"
               v-model="isFormValid"
            >
               <!-- <v-row>
                  <v-col class="font-italic">
                     <span class="font-weight-bold">NOTE: </span><span>Wherever is marked with <v-icon small>data_object</v-icon>, you can use variables with the following syntax to customize your email: <code>{{variableSyntax}}</code></span>
                  </v-col>
               </v-row> -->
               <v-row class="pt-2 mt-0">
                  <v-col xs="12" sm="12" :md="fromColSize">
                        <!-- :rules="[rules.required, rules.fromAddress]" -->
                     <v-text-field dense persistent-hint
                        autocomplete="off"
                        ref="fromAddress"
                        label="* From Email Address"
                        hint="Specify a valid email address"
                        :rules="[rules.required, rules.email]"
                        v-model="formData.fromAddress"
                     ></v-text-field>
                  </v-col>
                  <v-col v-if="formData.fromAddress"
                     xs="12" sm="4" md="5"
                  >
                     <v-text-field clearable dense persistent-hint
                        autocomplete="off"
                        ref="fromName"
                        label="From Name"
                        hint="Use something recognizable like your company name"
                        :rules="[rules.noVariables]"
                        v-model="formData.fromName"
                     ></v-text-field>
                  </v-col>
               </v-row>
               <v-row v-if="formData.fromAddress" class="pt-4 mt-0">
                  <v-col xs="12" sm="12" md="7">
                     <!-- md-variable -->
                     <v-text-field dense persistent-hint
                        autocomplete="off"
                        ref="toAddress"
                        label="* To Email Address(es)"
                        :hint="toAddressHint"
                        :placeholder="toAddressPlaceholder"
                        :rules="[rules.required, rules.toAddress]"
                        v-model="formData.toAddress"
                     >
                        <template #append>
                           <v-icon small>data_object</v-icon>
                        </template>
                     </v-text-field>
                  </v-col>
                  <v-col xs="12" sm="12" md="5">
                     <!-- :rules="[rules.required, rules.toAddress]" -->
                     <v-text-field dense persistent-hint
                        autocomplete="off"
                        ref="replyToAddress"
                        label="* Reply-to Email Address"
                        hint="A valid email address. Use the same domain as your 'From Email Address'"
                        :rules="[rules.required, rules.email]"
                        v-model="formData.replyToAddress"
                     ></v-text-field>
                  </v-col>
               </v-row>
               <v-row v-if="formData.fromAddress" class="pt-4 mt-0">
                  <v-col xs="12" sm="12" md="7">
                     <v-text-field dense persistent-hint
                        autocomplete="off"
                        ref="subject"
                        label="* Subject"
                        hint="Keep it relevant and non-spammy to avoid spam filters"
                        :rules="[rules.required]"
                        v-model="formData.subject"
                     >
                        <template #append>
                           <v-icon small>data_object</v-icon>
                        </template>
                     </v-text-field>
                  </v-col>
                  <v-col xs="12" sm="12" md="5">
                     <v-text-field dense persistent-hint
                        autocomplete="off"
                        ref="categories"
                        label="Categories"
                        hint="Separate categories with comma. Don't use 'Bulk' or 'Trigger'"
                        :rules="[rules.categories, rules.noVariables]"
                        v-model="formData.categories"
                     ></v-text-field>
                  </v-col>
               </v-row>
               <v-row v-if="formData.fromAddress" class="pt-5 mt-0">
                  <v-col xs="12" sm="12" md="6">
                     <v-textarea outlined persistent-hint
                        autocomplete="off"
                        ref="htmlBody"
                        label="HTML Body"
                        hint=""
                        rows="4"
                        :rules="[rules.body(formData)]"
                        v-model="formData.htmlBody"
                     >
                        <template #append>
                           <v-icon small>data_object</v-icon>
                        </template>
                     </v-textarea>
                     <!-- <v-btn text
                        class="px-3 no-uppercase"
                        color="blue darken-1"
                        :disabled="hasHtmlUnsubscribe"
                        @click="insertHtmlUnsubscribe"
                     >Insert Unsubscribe to your HTML Body</v-btn> -->
                  </v-col>
                  <v-col xs="12" sm="12" md="6">
                     <v-textarea outlined persistent-hint
                        autocomplete="off"
                        ref="textBody"
                        label="Text Body"
                        hint="This plain-text email is displayed if recipients can't display your HTML email"
                        rows="4"
                        :rules="[rules.body(formData)]"
                        v-model="formData.textBody"
                     >
                        <template #append>
                           <v-icon small>data_object</v-icon>
                        </template>
                     </v-textarea>
                     <!-- <v-btn text 
                        class="px-3 no-uppercase"
                        color="blue darken-1"
                        :disabled="hasTextUnsubscribe"
                        @click="insertTextUnsubscribe"
                     >Insert Unsubscribe to your Text Body</v-btn> -->
                  </v-col>
               </v-row>
            </v-form>
         </v-card-text>

         <v-card-actions class="pt-0">
            <div class="flex-grow-1"></div>
            <v-btn text small
               class="px-0"
               color="blue darken-1"
               @click="cancelClicked"
            >Cancel</v-btn>
            <v-btn text small
               class="px-0 mx-0"
               color="blue darken-1"
               :disabled="!this.isFormValid"
               @click="saveClicked"
            >Save</v-btn>
         </v-card-actions>
      </v-card>
   </v-dialog>

</div>
</template>

<script>
const NAME = "BtSendgridEmail";
// // const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const EMAIL_PATTERN = /^[\w+-.%]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
// const FROM_PATTERN = /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,9})|(##[^#]+##)$/;
// const FROM_PATTERN = /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,9})|(\{[^{}]+\{)$/;
// const FROM_PATTERN = /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,9})|(\{[^{}]+\{)$/;
// const TO_PATTERN = /^((([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,9})|(##[^#]+##)),?\s*)+$/;
// const TO_PATTERN = /^((([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,9})|(\{{[^{}]+\}})),?\s*)+$/; //Not good
// const TO_PATTERN = /^(([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,9}),?\s*)*(\{{[^{}]+\}})?(,\s*([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,9}))*$/


// const REPLY_TO_PATTERN = /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,9})$/;
const NO_VAR_PATTERN = /^(?!.*\{{[^{}]*\}}).*$/;
// // const VAR_PATTERN = /^\{\{\s*[a-zA-Z0-9-]+\s*\}\}$/;
// const VAR_PATTERN = /^\{\{(\w+)(?:::(.*?))?\}\}$/;
const VAR_WITH_DEFAULT_PATTERN = /^\{\{(\w+)(?:::(\w+))?\}\}$/;   //V250225
const HTML_UNSUBSCRIBE_PATTERN = /<a\s+href=["']\{\{unsubscribe\}\}["']\s*>(.*?)<\/a>/i;

class FormData {
   constructor(initVal) {
      this.fromAddress = initVal.fromAddress || '';
      this.fromName = initVal.fromName || '';
      this.toAddress = initVal.toAddress || '';
      this.replyToAddress = initVal.replyToAddress || '';
      this.subject = initVal.subject || '';
      this.htmlBody = initVal.htmlBody || '';
      this.textBody = initVal.textBody || '';
      this.categories = initVal.categories || '';
   }
}

export default {
   name: NAME,

   props: {
      value: {
         type: Object,
         default: () => {}
      },
      appName: {
         type: String,
         default: "Email - Sendgrid"
      },
      btnIconName: {
         type: String,
         default: "more_vert"
      },
      btnTooltip: {
         type: String,
         default: "click to configure"
      },
      debug: {
         type: Boolean,
         default: false
      },
      isActualEndpoint: {
         type: Boolean,
         default: true
      }
   },

   data() {
      return {
         rules: {
            required: value => !!value || "Value is required!",
            email: value => !value || EMAIL_PATTERN.test(value) || "Invalid email address!",
            // fromAddress: value => !value || FROM_PATTERN.test(value) || "Invalid email address!",
            // toAddress: value => !value || TO_PATTERN.test(value) || "Invalid email address or variable!",
            toAddress: value => {
               const addresses = value.split(',').map(addr => addr.trim());
               if (addresses.some(addr => !addr)) return 'Value cannot be empty!';
               for (const address of addresses) {
                  if (!this.isValidEmail(address) && !this.isValidVariable(address))
                     return `'${address}' is not a valid email or variable!`;
               }
               return true;
            },
            // replyToAddress: value => !value || REPLY_TO_PATTERN.test(value) || "Invalid email address!",
            categories: value => {
               let result = '';
               let cnt = 0;
               if (value.trim()) {
                  value.split(',').forEach(val => {
                     const v = val?.trim()?.toLowerCase();
                     if (!v || v === 'bulk' || v === 'trigger')
                        result += `'${v}' is invalid! `;
                     else if (v.length > 255)
                        result += `'${v}' is longer than 255! `;
                     else
                        cnt++;
                  });
               }
               return result || cnt <= 9 || 'Max number of categories can be 9!';
            },
            body(fd) {
               if (fd.htmlBody) return true;
               else if (fd.textBody) return true;
               else return 'One of the HTML or text bodies should have value!';
            },
            noVariables: value => !value || NO_VAR_PATTERN.test(value) || "Value cannot have variables!",
         },
         formData: {},
         dialog: false,
         isFormValid: false,
         toAddressHint: `For bulk: A variable. For trigger: A comma-separated list of variables and/or valid email address(es).`,
         toAddressPlaceholder: "{{email}}",
         variableSyntax: "{{variable-name}}",
         unsubscribeSyntax: "{{unsubscribe}}"
      }
   },

   computed: {
      isLocal() {
         return window.location.href.includes('http://localhost:8181/');
      },

      fromColSize() {
         return this.formData.fromAddress ? 7 : 12;
      },

      hasHtmlUnsubscribe() {
         // return !this.formData.htmlBody?.trim() || this.formData.htmlBody?.toLowerCase().includes(this.unsubscribeSyntax);
         return !this.formData.htmlBody?.trim() || this.formData.htmlBody.match(HTML_UNSUBSCRIBE_PATTERN);
      },

      hasTextUnsubscribe() {
         return !this.formData.textBody?.trim() || this.formData.textBody?.toLowerCase().includes(this.unsubscribeSyntax);
      }
   },

   watch: {},

   methods: {
      log(msg, showAlert) {
         if (this.debug) {
            console.log(`-----${NAME} V250813.1 => ${msg}`);
            if (showAlert)
               alert(`-----${NAME} V250813.1 says:\n${msg}`);
         }
      },

      async btnClicked() {
         // alert(JSON.stringify(this.value))
         this.formData = new FormData(this.value.instruction || {});
         this.dialog = true;
         setTimeout(() => {
            this.$refs.fromAddress.focus();
         }, 10);
      },

      populateSettings() {
         this.formData = new FormData({
            "fromAddress": "from@mindfireinc.com",
            "fromName": "Behzad Talebpour",
            "toAddress": "{{email}}",
            "replyToAddress": "reply-to@mindfireinc.com",
            "subject": "a message for {{Name}} from {{countries}}",
            "categories": "Behzad",
            "htmlBody": "<b>Hi {{Name}} from {{City}} of {{State}}!</b>",
            "textBody": "Hi {{name}} from {{city}} of {{state}}!"
         });
      },

      isValidEmail(email) {
         if (!email) return false;
         return EMAIL_PATTERN.test(email);
      },

      isValidVariable(v) {
         if (!v) return false;
         return VAR_WITH_DEFAULT_PATTERN.test(v);// && !v.includes(':');
      },

      insertHtmlUnsubscribe() {
         if (!this.hasHtmlUnsubscribe)
            this.formData.htmlBody += `\n<br /><br />If you no longer wish to receive these emails, you can <a href="{{unsubscribe}}">unsubscribe here</a>.`;
      },
      insertTextUnsubscribe() {
         if (!this.hasTextUnsubscribe)
            this.formData.textBody += `\n\nIf you no longer wish to receive these emails, you can unsubscribe at: {{unsubscribe}}`;
      },

      saveClicked() {
         if (!this.$refs.mainForm.validate()) return;
         
         const unsubscribeMsg = this.validateUnsubscribeVar();
         // if (unsubscribeMsg && !confirm(`${unsubscribeMsg}\n\nDo you still want to proceed?`)) {
         //    return;
         // }
         if (unsubscribeMsg && !confirm(`${unsubscribeMsg}\n\nDo you want to proceed and save the changes?`)) {
            return;
         }

         const settings = {
            fromAddress: this.formData.fromAddress,
            toAddress: this.formData.toAddress.split(',').map(a => a.trim()).join(','),
            replyToAddress: this.formData.replyToAddress,
            subject: this.formData.subject.trim(),
         };

         if (this.formData.fromName.trim())
            settings.fromName = this.formData.fromName.trim();
         if (this.formData.categories.trim())
            settings.categories = this.formData.categories.split(',').map(c => c.trim()).join(',');
         if (this.formData.htmlBody.trim())
            settings.htmlBody = this.formData.htmlBody.trim();
         if (this.formData.textBody.trim())
            settings.textBody = this.formData.textBody.trim();
            
         const emitObj = {
            instruction: settings,
            consumerCode: 'bff557d9-2941-4ee2-ab07-866a22ff71dd',
            appEndpoint: ' '
            // appEndpoint: 'https://slpiyz6zyz2tokbebmncvksi2y0nlrlw.lambda-url.us-east-1.on.aws/'
            // appEndpoint: 'http://internal-alb-prd-internal-219971537.us-west-1.elb.amazonaws.com/api/outboundapp/SubmitOutboundAppData?serviceType=2030'
         }

         this.$emit('change', emitObj);
         this.$emit('input', emitObj);
         this.cancelClicked();
      },

      validateUnsubscribeVar() {
         // const hasHtmlBodyUnsubscribe = this.hasUnsubscribe(this.formData.htmlBody);
         // const hasTextBodyUnsubscribe = this.hasUnsubscribe(this.formData.textBody);
         let warningMsg = '';

         // if (!this.hasHtmlUnsubscribe && !this.hasTextUnsubscribe)
         //    warningMsg = 'Neither the HTML nor text body includes the unsubscribe option.';
         // else if (!this.hasHtmlUnsubscribe)
         //    warningMsg = 'The HTML body is missing the unsubscribe option.';
         // else if (!this.hasTextUnsubscribe)
         //    warningMsg = 'The text body is missing the unsubscribe option.';

         if (!this.hasHtmlUnsubscribe && !this.hasTextUnsubscribe) {
            this.insertHtmlUnsubscribe();
            this.insertTextUnsubscribe();
            warningMsg = "We've added the unsubscribe link to both the HTML and Text bodies.";
         } else if (!this.hasHtmlUnsubscribe) {
            this.insertHtmlUnsubscribe();
            warningMsg = "We've added the unsubscribe link to the HTML Body.";
         } else if (!this.hasTextUnsubscribe) {
            warningMsg = "We've added the unsubscribe link to the Text Body.";
            this.insertTextUnsubscribe();
         }

         return warningMsg;
      },

      // hasUnsubscribe(body) {
      //    return !body?.trim() || body?.toLowerCase().includes(this.unsubscribeSyntax);
      // },

      cancelClicked() {
         this.$refs.mainForm.resetValidation();
         this.dialog = false;
      }
   },

   created() {
      // this.log(`in created(): debug=${this.debug}, isActualEndpoint=${this.isActualEndpoint}, appName=${this.appName}`, true);
   },

   mounted() { }
}
</script>

<style scoped>
.no-uppercase {
   text-transform: none;
}
</style>